import debounce from "lodash.debounce";
import dayjs, { Dayjs } from "dayjs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { DateHelper } from "@/helpers";
import { useAppDispatch, useAppSelector } from "@/stores";
import { TransferQueryParams } from "@/domains/transfer";
import { tenantTransfersActions, tenantTransfersSelects } from "./store";
// components
import { DatePicker, Form, Input } from "antd";
import {
  DateRangeRadioGroup,
  DateRangeRadioGroupChangeEvent,
} from "@/components";
import { DateRange } from "@/domains/common";

type TransferQueryParamsFormValues = QueryParamsFormValues<TransferQueryParams>;

type TransfersFormData = Pick<TransferQueryParamsFormValues, "id" | "order"> & {
  dateRange?: [Dayjs, Dayjs];
};

export const TableTopFilters = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [form] = Form.useForm<TransfersFormData>();

  const queryParams = useAppSelector(tenantTransfersSelects.queryParams);

  const dateRange = useAppSelector(tenantTransfersSelects.dateRange);

  useEffect(() => {
    form.setFieldsValue({
      id: queryParams.id,
      order: queryParams.order,
      dateRange: [
        dayjs(queryParams.recoged_started_at),
        dayjs(queryParams.recoged_stopped_at),
      ],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const convertToQueryParams = (
    values: TransfersFormData,
  ): Pick<
    TransferQueryParamsFormValues,
    "id" | "order" | "recoged_started_at" | "recoged_stopped_at"
  > => ({
    id: values.id,
    order: values.order,
    recoged_started_at: values.dateRange?.[0],
    recoged_stopped_at: values.dateRange?.[1],
  });

  const search = debounce(
    (
      values: Pick<
        TransferQueryParamsFormValues,
        "id" | "order" | "recoged_started_at" | "recoged_stopped_at"
      >,
    ) => {
      const newQueryParams = DateHelper.convertDayjsToString(values);
      console.log(newQueryParams);
      dispatch(tenantTransfersActions.setQueryParams(newQueryParams));
    },
    400,
  );

  const handleValuesChange = (_: unknown, values: TransfersFormData) => {
    search(convertToQueryParams(values));
  };

  const handleDateRangeRadioGroupChange: DateRangeRadioGroupChangeEvent = (
    value,
    getDateRange,
  ) => {
    dispatch(tenantTransfersActions.setDateRange(value));
    if (!getDateRange) return;

    const range = getDateRange();
    form.setFieldsValue({
      ...form.getFieldsValue(),
      dateRange: range,
    });

    handleValuesChange(null, form.getFieldsValue());
  };

  const handleDateRangeChange = () => {
    dispatch(tenantTransfersActions.setDateRange(DateRange.custom));
  };

  return (
    <Form
      form={form}
      labelCol={{ className: "w:80" }}
      onValuesChange={handleValuesChange}
    >
      <div className="flex ai:center gap:16">
        <Form.Item
          label={t("domains.Transfer.systemId")}
          name="id"
          className="flex:1"
        >
          <Input
            allowClear
            placeholder={t("systems.Placeholder.allowFuzzySearch")}
          />
        </Form.Item>
        <Form.Item
          label={t("domains.Transfer.orderId")}
          name="order"
          className="flex:1"
        >
          <Input
            allowClear
            placeholder={t("systems.Placeholder.allowFuzzySearch")}
          />
        </Form.Item>
        <Form.Item
          label={t("systems.Date.dateRange")}
          name="dateRange"
          className="flex:1"
        >
          <DatePicker.RangePicker
            showTime
            format="YYYY-MM-DD HH:mm"
            onChange={handleDateRangeChange}
          />
        </Form.Item>
        <div className="mb:16 flex ai:center">
          <DateRangeRadioGroup
            dateRange={dateRange}
            onChange={handleDateRangeRadioGroupChange}
          />
        </div>
      </div>
    </Form>
  );
};
